<template>
  <div class="container-fluid py-5">
    <!-- Header con información del propietario -->
    <div class="row mb-5" v-if="propietario">
      <div class="col-12">
        <div class="card border-light">
          <div class="card-body d-flex align-items-center gap-4">
            <div v-if="propietario.logo_url" class="flex-shrink-0">
              <img
                :src="propietario.logo_url"
                :alt="propietario.razon_social"
                style="max-height: 60px; object-fit: contain"
              />
            </div>
            <div class="flex-grow-1">
              <h3 class="mb-1">{{ propietario.razon_social }}</h3>
              <p class="text-muted mb-0">Subastas privadas</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mensajes de estado -->
    <div class="row mb-4" v-if="loading">
      <div class="col-12">
        <div class="text-center py-5">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-4" v-if="!loading && error">
      <div class="col-12">
        <div class="alert alert-danger" role="alert">
          <h5>{{ error }}</h5>
          <p v-if="error.includes('autorizado')">
            Debes estar autenticado con un email verificado y autorizado para
            acceder a estas subastas.
          </p>
        </div>
      </div>
    </div>

    <div class="row mb-4" v-if="!loading && !error && subastas.length === 0">
      <div class="col-12">
        <div class="alert alert-info" role="alert">
          No hay subastas privadas disponibles en este momento.
        </div>
      </div>
    </div>

    <!-- Listado de subastas -->
    <div class="row" v-if="!loading && !error && subastas.length > 0">
      <div
        v-for="subasta in subastas"
        :key="subasta.id"
        class="col-md-6 col-lg-4 mb-4"
      >
        <div class="card h-100 hover-shadow">
          <div v-if="subasta.imagen" class="card-img-container">
            <img
              :src="`${storage_path}/${subasta.imagen.hash}.${subasta.imagen.extension}`"
              class="card-img-top"
              :alt="subasta.titulo"
            />
            <div class="position-absolute top-0 end-0 p-2">
              <span class="badge bg-soft-orange text-soft-orange">
                Subasta interna
              </span>
            </div>
          </div>
          <div class="card-body">
            <h5 class="card-title">{{ subasta.titulo }}</h5>
            <p class="card-text text-muted small">
              {{ subasta.subtitulo }}
            </p>

            <div class="mb-3">
              <small class="text-muted d-block">
                <i class="fa-regular fa-calendar me-1"></i>
                {{ formatearFecha(subasta.fecha_hora_inicio) }}
              </small>
              <small class="text-muted d-block">
                <i class="fa-regular fa-clock me-1"></i>
                {{ formatearHora(subasta.fecha_hora_inicio) }}
              </small>
            </div>

            <div class="d-flex gap-2">
              <button
                class="btn btn-sm btn-primary flex-grow-1"
                @click="irASubasta(subasta.id)"
              >
                Ver subasta
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginación -->
    <div class="row mt-5" v-if="!loading && !error && totalPages > 1">
      <div class="col-12 d-flex justify-content-center">
        <nav>
          <ul class="pagination">
            <li
              class="page-item"
              :class="{ disabled: currentPage === 1 }"
            >
              <button
                class="page-link"
                @click="cambiarPagina(currentPage - 1)"
                :disabled="currentPage === 1"
              >
                Anterior
              </button>
            </li>

            <li
              v-for="page in pageNumbers"
              :key="page"
              class="page-item"
              :class="{ active: page === currentPage }"
            >
              <button
                class="page-link"
                @click="cambiarPagina(page)"
              >
                {{ page }}
              </button>
            </li>

            <li
              class="page-item"
              :class="{ disabled: currentPage === totalPages }"
            >
              <button
                class="page-link"
                @click="cambiarPagina(currentPage + 1)"
                :disabled="currentPage === totalPages"
              >
                Siguiente
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import api from "@/api/axios";
import { getFormattedDate, getFormattedTime } from "@/utils/helpers";

const route = useRoute();

const propietarioSlug = route.params.slug;
const storage_path = process.env.VUE_APP_STORAGE_PATH;

const propietario = ref(null);
const subastas = ref([]);
const loading = ref(true);
const error = ref(null);
const currentPage = ref(1);
const totalPages = ref(1);

const pageNumbers = computed(() => {
  const pages = [];
  const maxPages = Math.min(5, totalPages.value);
  const startPage = Math.max(1, currentPage.value - Math.floor(maxPages / 2));
  const endPage = Math.min(totalPages.value, startPage + maxPages - 1);

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }
  return pages;
});

const formatearFecha = (fecha) => {
  return getFormattedDate(fecha) || "A confirmar";
};

const formatearHora = (fecha) => {
  return getFormattedTime(fecha) ? `${getFormattedTime(fecha)} hs` : "";
};

const cargarSubastas = async (page = 1) => {
  try {
    loading.value = true;
    error.value = null;

    const response = await api.get(
      `/subastas-privadas/${propietarioSlug}?page=${page}`
    );

    subastas.value = response.data.data;
    currentPage.value = response.data.current_page;
    totalPages.value = response.data.last_page;
  } catch (err) {
    if (err.response?.status === 404) {
      error.value = "Propietario no encontrado";
    } else if (err.response?.status === 403) {
      error.value =
        "No estás autorizado para ver estas subastas. Verifica que tu email esté verificado y autorizado.";
    } else {
      error.value =
        err.response?.data?.error || "Error al cargar las subastas privadas";
    }
  } finally {
    loading.value = false;
  }
};

const cargarPropietario = async () => {
  try {
    const response = await api.get(`/propietarios-slug/${propietarioSlug}`);
    propietario.value = response.data;
  } catch (err) {
    console.error("Error cargando propietario:", err);
  }
};

const cambiarPagina = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    currentPage.value = newPage;
    cargarSubastas(newPage);
  }
};

const irASubasta = (subastaId) => {
  // Navegar a la ficha de subasta
  window.location.href = `/subastas/lotes?id=${subastaId}`;
};

onMounted(async () => {
  await cargarPropietario();
  await cargarSubastas();
});
</script>

<style scoped>
.hover-shadow {
  transition: box-shadow 0.3s ease;
}

.hover-shadow:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.card-img-container {
  position: relative;
  overflow: hidden;
  height: 200px;
}

.card-img-top {
  height: 100%;
  object-fit: cover;
}

.badge-soft-orange {
  background-color: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.text-soft-orange {
  color: #ffc107;
}
</style>
